import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { track } from '@/utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30 * 1000;
const QUALITY_SCORE = 70;
const SESSION_FLAG = 'tp360_first_real_signal_shown';

/**
 * Surfaces a one-time-per-session toast when the **first** real high-quality signal
 * (score ≥ 70) arrives while the user is logged in. Sample / demo trades are not
 * signals so they never trigger this; it's the moment users transition from
 * "I tried the demo" → "the system is actually finding setups for me".
 *
 * Behaviour:
 *  - On mount, snapshot the latest signal IDs as the "baseline" — anything already
 *    in history doesn't trigger; only newly-arriving signals do.
 *  - Poll /api/signals every 30s; trigger on first new signal where score ≥ 70.
 *  - sessionStorage flag prevents re-firing across SPA navigations / re-renders.
 *  - Optional onNavigate callback receives the signal so the parent can route
 *    the user to the Signal Scanner.
 */
export default function useFirstRealSignalMoment({ enabled = true, onNavigate, token = null } = {}) {
  const baselineRef = useRef(null); // Set<id>; null = not yet seeded
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    if (sessionStorage.getItem(SESSION_FLAG) === '1') {
      firedRef.current = true;
      return undefined;
    }

    let cancelled = false;
    let timer = null;

    const fetchSignals = async () => {
      try {
        const res = await fetch(`${API_URL}/api/signals?limit=15`);
        if (!res.ok) return;
        const signals = await res.json();
        if (cancelled) return;

        // First poll seeds the baseline of "already there before user logged in".
        if (baselineRef.current === null) {
          baselineRef.current = new Set(signals.map((s) => s.id));
          return;
        }

        if (firedRef.current) return;

        const fresh = signals.find(
          (s) =>
            !baselineRef.current.has(s.id) &&
            typeof s.score === 'number' &&
            s.score >= QUALITY_SCORE
        );
        if (!fresh) return;

        firedRef.current = true;
        sessionStorage.setItem(SESSION_FLAG, '1');

        track('first_real_signal_seen', {
          signal_id: fresh.id,
          symbol: fresh.symbol,
          score: fresh.score,
        });

        // Probe automation state so we can decide whether to surface a secondary
        // "Enable automation" action. Best-effort — defaults to "on" if it fails.
        let automationOn = true;
        try {
          const sRes = await fetch(`${API_URL}/api/paper-trading/settings`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
          if (sRes.ok) {
            const s = await sRes.json();
            automationOn = !!s.enabled && !s.emergency_stop;
          }
        } catch {
          /* assume on */
        }

        const enableAutomationFromSignal = async () => {
          try {
            // Re-fetch the full settings doc and POST back with enabled:true
            const headers = token
              ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
              : { 'Content-Type': 'application/json' };
            const sRes = await fetch(`${API_URL}/api/paper-trading/settings`, {
              headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });
            if (!sRes.ok) throw new Error('settings fetch failed');
            const s = await sRes.json();
            await fetch(`${API_URL}/api/paper-trading/settings`, {
              method: 'POST',
              headers,
              body: JSON.stringify({ ...s, enabled: true }),
            });
            track('automation_enabled_from_signal', { signal_id: fresh.id });
            toast.success('Automation enabled — scanning markets…');
          } catch {
            toast.error('Could not enable automation — try the toggle on Paper Trading.');
          }
        };

        const titleNode = (
          <div className="flex items-center gap-2">
            <span
              className="text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500 border border-emerald-500/30 whitespace-nowrap shrink-0"
              title="Live setup from the scanner"
            >
              Real
            </span>
            <span className="font-semibold">New high-quality setup detected</span>
          </div>
        );

        toast.success(titleNode, {
          description: `${fresh.symbol} · ${fresh.signal} · score ${fresh.score}. Live setup — automation can take this trade for you.`,
          duration: 12000,
          style: { minWidth: '420px' },
          action: onNavigate
            ? {
                label: 'View live setup',
                onClick: () => onNavigate(fresh),
              }
            : undefined,
          cancel: !automationOn
            ? {
                label: 'Enable automation',
                onClick: enableAutomationFromSignal,
              }
            : undefined,
        });
      } catch {
        /* network blip — try again next tick */
      }
    };

    fetchSignals();
    timer = setInterval(fetchSignals, POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [enabled, onNavigate, token]);
}
